/**
 * Build prompt sections from company Google Doc and selected Drive files.
 */
export function buildGoogleContextPromptSections({
  includeCompanyGoogleDoc = false,
  companyGoogleDocContent = null,
  selectedDriveFileIds = new Set(),
  driveFileContents = {},
}) {
  let sections = '';

  if (includeCompanyGoogleDoc && companyGoogleDocContent?.text) {
    const title = companyGoogleDocContent.title || 'Company Google Doc';
    sections += `COMPANY GOOGLE DOC (${title}):\n${companyGoogleDocContent.text}\n\n`;
  }

  if (selectedDriveFileIds.size > 0) {
    const fileSections = [];
    selectedDriveFileIds.forEach((fileId) => {
      const file = driveFileContents[fileId];
      if (file?.text) {
        fileSections.push(`--- ${file.name || 'Drive file'} ---\n${file.text}`);
      }
    });

    if (fileSections.length > 0) {
      sections += `COMPANY GOOGLE DRIVE FILES:\n${fileSections.join('\n\n')}\n\n`;
    }
  }

  return sections;
}

export function estimateGoogleContextTokens({
  includeCompanyGoogleDoc = false,
  companyGoogleDocContent = null,
  selectedDriveFileIds = new Set(),
  driveFileContents = {},
}) {
  const text = buildGoogleContextPromptSections({
    includeCompanyGoogleDoc,
    companyGoogleDocContent,
    selectedDriveFileIds,
    driveFileContents,
  });

  if (!text) return 0;
  return Math.ceil(text.length / 4);
}

/** Short type badge shown beside a Drive file. */
export function getDriveFileIconLabel(mimeType, name = '') {
  const mime = String(mimeType || '').toLowerCase();
  const lower = String(name || '').toLowerCase();
  if (mime.includes('folder')) return 'DIR';
  if (
    mime.includes('spreadsheet') ||
    mime.includes('excel') ||
    mime.includes('spreadsheetml') ||
    /\.xlsx?$/.test(lower)
  ) {
    return 'XLS';
  }
  if (mime === 'text/csv' || lower.endsWith('.csv')) return 'CSV';
  if (mime.includes('presentation') || /\.pptx?$/.test(lower)) return 'PPT';
  if (mime.includes('document') || /\.docx?$/.test(lower)) return 'DOC';
  if (mime === 'application/pdf' || lower.endsWith('.pdf')) return 'PDF';
  if (mime.startsWith('image/') || /\.(png|jpe?g|gif|webp)$/.test(lower)) return 'IMG';
  if (mime.startsWith('video/') || /\.(mp4|mov|webm)$/.test(lower)) return 'MP4';
  if (/\.(tsx?|jsx?|py|json)$/.test(lower)) return 'CODE';
  if (mime.startsWith('text/') || /\.(txt|md)$/.test(lower)) return 'TXT';
  return 'FILE';
}

export function isSpreadsheetDriveFile(mimeType, name = '') {
  const label = getDriveFileIconLabel(mimeType, name);
  return label === 'XLS' || label === 'CSV';
}

export function driveFolderLabel(folderPath) {
  const parts = Array.isArray(folderPath) ? folderPath.filter(Boolean) : [];
  return parts.length ? parts.join(' / ') : 'This folder';
}

/** Parse CSV (or a single-sheet export) into a small grid for preview. */
export function parseSpreadsheetPreview(text, { maxRows = 10, maxCols = 8 } = {}) {
  const source = String(text || '').replace(/^\uFEFF/, '');
  if (!source.trim()) return { columns: [], rows: [], truncated: false };

  const records = [];
  let row = [];
  let cell = '';
  let inQuotes = false;

  for (let i = 0; i < source.length; i += 1) {
    const char = source[i];
    const next = source[i + 1];
    if (inQuotes) {
      if (char === '"' && next === '"') {
        cell += '"';
        i += 1;
      } else if (char === '"') {
        inQuotes = false;
      } else {
        cell += char;
      }
      continue;
    }
    if (char === '"') {
      inQuotes = true;
    } else if (char === ',') {
      row.push(cell);
      cell = '';
    } else if (char === '\n' || char === '\r') {
      if (char === '\r' && next === '\n') i += 1;
      row.push(cell);
      cell = '';
      if (row.some((value) => String(value).trim() !== '')) records.push(row);
      row = [];
    } else {
      cell += char;
    }
  }
  row.push(cell);
  if (row.some((value) => String(value).trim() !== '')) records.push(row);

  const width = Math.min(
    maxCols,
    records.reduce((max, record) => Math.max(max, record.length), 0)
  );
  const clipped = records.slice(0, maxRows + 1).map((record) => record.slice(0, width));
  const columns = (clipped[0] || []).map((value, index) => String(value || '').trim() || `Column ${index + 1}`);
  return {
    columns,
    rows: clipped.slice(1),
    truncated: records.length > maxRows + 1 || records.some((record) => record.length > maxCols),
  };
}
